import { useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import OrderSearchCard from "../evidenceBuilder/OrderSearchCard";
import OrderCandidates from "../evidenceBuilder/OrderCandidates";
import { useOrderSearch } from "../../hooks/useOrderSearch";
import { apiErrorMessage } from "../../utils/apiError";
import { EvidenceOrderBody, OrderCandidate } from "../../types/evidenceBankCase";

const DEFAULT_DESCRIPTION = "The portal could not find the order behind this dispute. Search for it and the document is built from the order you pick.";

// The resolver could not name the dispute's order; the agent finds it and the draft is built on it.
export default function FindOrderDialog({ disputeId, onClose, onPick, searchPath = `/api/disputes/${disputeId}/evidence-order-search`, description = DEFAULT_DESCRIPTION }: {
  disputeId: string;
  onClose: () => void;
  /** Rejects with the API's reason when the order is refused. */
  onPick: (body: EvidenceOrderBody) => Promise<void>;
  searchPath?: string;
  description?: string;
}) {
  const { searching, searchError, found, findOrder } = useOrderSearch(searchPath);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (candidate: OrderCandidate) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await onPick({ order: { source: candidate.source, accountId: candidate.accountId, orderKey: candidate.orderKey, orderName: candidate.orderName } });
      onClose();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to use this order"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={(_, reason) => { if (reason !== "backdropClick" && !saving) onClose(); }}
      maxWidth="md"
      fullWidth
      aria-labelledby="find-order-title"
    >
      <DialogTitle id="find-order-title">Find the order</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          <OrderSearchCard searching={searching} onSearch={findOrder} />
          {searchError && <Alert severity="error">{searchError}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {saving && <CircularProgress size={20} />}
          {found && (
            <Box sx={{ pointerEvents: saving ? "none" : "auto", opacity: saving ? 0.6 : 1 }}>
              <OrderCandidates result={found.result} searchedBy={found.by} onContinue={(candidate) => { void pick(candidate); }} />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
